import OutlineTitle from "@/components/OutlineTitle";
import { useEffect, useState } from "react";
import { simhubAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trophy, Flag, Gauge, Users, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const formatLapTime = (ms) => {
  if (!ms || ms <= 0) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
};

const rankColor = (r) => (r === 1 ? "#FFD700" : r === 2 ? "#C0C0C0" : r === 3 ? "#CD7F32" : "hsl(var(--muted-foreground))");

export default function Leaderboards() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [board, setBoard] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const [loadingBoard, setLoadingBoard] = useState(false);

  useEffect(() => {
    simhubAPI.getLeaderboardTracks()
      .then((r) => {
        const list = r.data || [];
        setTracks(list);
        if (list.length && !selectedKey) setSelectedKey(list[0].track_key);
      })
      .catch(() => setTracks([]))
      .finally(() => setLoadingTracks(false));
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    setLoadingBoard(true);
    setBoard(null);
    simhubAPI.getLeaderboard(selectedKey)
      .then((r) => setBoard(r.data))
      .catch(() => setBoard({ rows: [] }))
      .finally(() => setLoadingBoard(false));
  }, [selectedKey]);

  const selectedMeta = tracks.find((t) => t.track_key === selectedKey);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <OutlineTitle>Leaderboards</OutlineTitle>
          <p className="text-sm text-muted-foreground">Best laps captured from SimHub sessions across the community.</p>
        </div>
        <Trophy className="h-8 w-8 text-primary" />
      </div>

      {loadingTracks ? (
        <Skeleton className="h-24 rounded-lg" />
      ) : tracks.length === 0 ? (
        <Card className="flex flex-col items-center border border-dashed border-border bg-card py-12 text-center">
          <Flag className="mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">No tracks published yet</p>
          <p className="text-sm text-muted-foreground">
            An admin needs to publish tracks from the SimHub Tracks admin panel.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-[minmax(0,320px)_1fr] md:items-center">
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger data-testid="leaderboard-track-picker">
                <SelectValue placeholder="Pick a track" />
              </SelectTrigger>
              <SelectContent>
                {tracks.map((t) => (
                  <SelectItem key={t.track_key} value={t.track_key} data-testid={`leaderboard-track-option-${t.track_key}`}>
                    {t.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedMeta && (
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {selectedMeta.racers} racers</span>
                <span className="flex items-center gap-1"><Gauge className="h-4 w-4" /> {selectedMeta.laps} laps recorded</span>
              </div>
            )}
          </div>

          <Card className="border-border bg-card">
            {loadingBoard || !board ? (
              <div className="space-y-2 p-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
            ) : board.rows.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">No laps recorded on this track yet.</p>
            ) : (
              <div className="divide-y divide-border">
                <div className="hidden grid-cols-[40px_1fr_120px_80px_80px_80px] items-center gap-3 px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground md:grid">
                  <span>#</span>
                  <span>Racer</span>
                  <span>Best lap</span>
                  <span className="text-center">ABS</span>
                  <span className="text-center">TC</span>
                  <span className="text-right">Car</span>
                </div>
                {board.rows.map((row, i) => {
                  const rank = i + 1;
                  const isMe = user?.id === row.user_id;
                  return (
                    <div
                      key={row.user_id}
                      className={`grid grid-cols-[40px_1fr_120px_80px] items-center gap-3 p-3 text-sm md:grid-cols-[40px_1fr_120px_80px_80px_80px] ${isMe ? "bg-primary/10" : ""}`}
                      data-testid={`leaderboard-row-${rank}`}
                    >
                      <span className="font-display text-lg font-bold" style={{ color: rankColor(rank) }}>{rank}</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={row.profile_image} />
                          <AvatarFallback className="bg-secondary text-xs">{(row.username || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          {row.profile_slug ? (
                            <Link to={`/u/${row.profile_slug}`} className="truncate font-medium hover:text-primary">
                              {row.username}
                            </Link>
                          ) : (
                            <span className="truncate font-medium">{row.username}</span>
                          )}
                          {isMe && <span className="ml-2 text-xs text-primary">You</span>}
                        </div>
                      </div>
                      <Badge className="w-fit border-0 bg-primary/20 font-mono text-primary">
                        {formatLapTime(row.lap_time_ms)}
                      </Badge>
                      <span className="text-center text-xs text-muted-foreground">
                        {row.abs_level != null ? row.abs_level : "—"}
                      </span>
                      <span className="hidden text-center text-xs text-muted-foreground md:inline">
                        {row.tc_level != null ? row.tc_level : "—"}
                      </span>
                      <span className="hidden truncate text-right text-xs text-muted-foreground md:inline" title={row.car_model || ""}>
                        {row.car_model || "—"}
                      </span>
                      {!isMe && row.session_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="col-span-4 mt-1 md:col-span-6 md:mt-0 md:col-start-1 md:row-start-1 md:ml-auto md:w-auto"
                          onClick={async () => {
                            try {
                              const r = await simhubAPI.createShareRequest(row.session_id, `Compare lap at ${board.display_name}`);
                              toast.success(r.data?.status === "approved" ? "Session is public — opening in SimHub" : "Request sent");
                            } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
                          }}
                          data-testid={`leaderboard-request-${rank}`}
                        >
                          <Send className="mr-1 h-3 w-3" /> Request session
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
